/*
 * DRAFT — PENDING LEGAL REVIEW. These Terms of Service were drafted for App Store
 * submission and have not yet been reviewed by counsel. Do not treat as final.
 * (Source comment only; not rendered on the page.)
 */
import React from 'react'
import { Link } from 'react-router-dom'
import { usePageMeta } from '@/hooks/usePageMeta'

const SUPPORT_EMAIL = 'claritas.support@gmail.com'

function Section({ id, number, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-bold font-display text-charcoal mb-4">
        <span className="text-gold">{number}</span> {title}
      </h2>
      <div className="space-y-4 text-base text-charcoal-muted leading-relaxed">
        {children}
      </div>
    </section>
  )
}

export default function Terms() {
  usePageMeta({
    title: 'Terms of Service | Claritas',
    description:
      'The terms that govern your use of Claritas Giving, including donations to parishes, Roundups, community features, and our zero-tolerance policy for objectionable content and abusive behavior.',
  })

  return (
    <div className="min-h-screen bg-background-light">
      {/* Hero */}
      <section className="bg-white py-20 border-b border-surface-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold tracking-widest uppercase text-gold mb-4">
            Legal
          </p>
          <div className="w-8 h-px bg-gold mx-auto mb-6" />
          <h1 className="text-4xl sm:text-5xl font-bold font-display text-charcoal mb-6">
            Terms of Service
          </h1>
          <p className="text-sm text-charcoal-muted">
            Effective Date: October 16, 2026
          </p>
          <p className="text-sm text-charcoal-muted mt-1">
            Support Email:{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="bg-background-light py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <p className="text-base text-charcoal-muted leading-relaxed">
            These Terms of Service (the "Terms") are a binding agreement between you and Claritas
            Giving ("Claritas," "we," "us," or "our") and govern your use of the Claritas platform,
            including our websites, mobile applications, and related services (collectively, the
            "Platform"). Claritas is a Catholic parish giving and community platform that lets you
            discover parishes, make one-time, recurring, and round-up donations, and follow parish
            events, announcements, and groups. By creating an account or using the Platform, you agree
            to these Terms and to our{' '}
            <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>. If you do
            not agree, do not use the Platform.
          </p>

          <Section id="eligibility" number="1" title="Eligibility and Accounts">
            <p>
              You must be at least 13 years old to use the Platform, and at least 18 years old (or the
              age of majority where you live) to make donations or link a bank account. You are
              responsible for providing accurate account information, keeping your sign-in credentials
              secure, and all activity that occurs under your account. Notify us promptly at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a>{' '}
              if you suspect unauthorized use of your account.
            </p>
          </Section>

          <Section id="donations" number="2" title="Donations">
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-charcoal">Donations go to the parish.</strong> When you donate,
                the recipient is the parish or organization you select, not Claritas. Claritas provides
                the technology that facilitates your gift. Payments are processed by{' '}
                <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Stripe, Inc.</a>,
                and your use of Stripe's services is also subject to Stripe's terms.
              </li>
              {/* LEGAL REVIEW */}
              <li>
                <strong className="text-charcoal">Merchant of record.</strong> The organization you
                give to is the merchant of record for your donation. Claritas processes the payment on
                the organization's behalf through Stripe Connect, and the organization's name may
                appear on your card statement.
              </li>
              {/* LEGAL REVIEW */}
              <li>
                <strong className="text-charcoal">Refunds.</strong> Donations are generally
                non-refundable. To request a refund, contact the organization you gave to. The
                organization submits the request to Claritas, and Claritas reviews and approves it
                before it is processed. Approved refunds go back to the original payment method, and
                you will receive an email confirmation.
              </li>
              <li>
                <strong className="text-charcoal">Recurring donations.</strong> Recurring donations
                continue on the schedule you choose until you cancel them in the app. Cancellation stops
                future charges; it does not reverse past donations.
              </li>
              {/* LEGAL REVIEW */}
              <li>
                <strong className="text-charcoal">Receipts and tax matters.</strong> Claritas emails a
                receipt for every successful donation on behalf of the receiving organization. Each
                January it also sends a year-end giving statement, and past statements can be
                downloaded in the app and on the web. Claritas does not provide tax advice.
              </li>
              <li>
                <strong className="text-charcoal">Fees.</strong> Any processing or platform fees are
                disclosed before you confirm a donation.
              </li>
            </ul>
          </Section>

          <Section id="roundups" number="3" title="Roundups and Bank Linking">
            <p>
              Roundups are optional. If you enable them, you authorize Claritas to access transaction
              data from the bank account you link through Stripe Financial Connections, calculate the
              round-up on eligible purchases, and charge the accumulated amount to your saved payment
              method on a periodic schedule for donation to the parish you select. You can pause
              Roundups or disconnect your bank account at any time in the app, which stops future
              round-up charges. You are responsible for ensuring you have sufficient funds; Claritas is
              not responsible for overdraft or bank fees.
            </p>
          </Section>

          <Section id="community" number="4" title="Community Content and Conduct">
            <p>
              Parishes may publish events, announcements, groups, and other content on the Platform,
              and users may interact with that content ("User Content"). You are responsible for any
              content you submit and must have the right to share it.
            </p>
            <p>
              <strong className="text-charcoal">
                Claritas has zero tolerance for objectionable content and abusive users.
              </strong>{' '}
              You agree not to post, share, or promote content that is unlawful, hateful,
              discriminatory, harassing, threatening, sexually explicit, violent, defamatory,
              fraudulent, spam, or that infringes the rights of others, and not to abuse, harass,
              impersonate, or defraud other users or parishes.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-charcoal">Reporting.</strong> You can report any content you
                believe violates these Terms directly from the app, or by emailing{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a>.
              </li>
              <li>
                <strong className="text-charcoal">Blocking.</strong> You can block a parish in the app
                so that its content no longer appears in your feed.
              </li>
              <li>
                <strong className="text-charcoal">Our response.</strong> We review reported content
                and act on reports within 24 hours. Content that violates these Terms will be removed,
                and the users or parish accounts responsible will be suspended or permanently removed
                from the Platform.
              </li>
            </ul>
          </Section>

          <Section id="parishes" number="5" title="Parish Accounts">
            <p>
              Parish administrators represent that they are authorized to act on behalf of their parish,
              that the information they provide is accurate, and that donations they receive will be used
              in accordance with applicable law. Parishes are responsible for the content they publish
              and for their handling of donor information. Parish accounts are subject to the conduct
              rules above and may be suspended or removed for violations.
            </p>
            {/* LEGAL REVIEW */}
            <p>
              <strong className="text-charcoal">Nonprofit verification.</strong> Organizations cannot
              sign themselves up. Claritas staff onboard each organization and verify its nonprofit
              status. Verification may include its legal name, EIN, IRS determination or group-ruling
              documents, and a diocesan inclusion letter.
            </p>
          </Section>

          <Section id="acceptable-use" number="6" title="Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Platform for any unlawful purpose, including money laundering or fraud.</li>
              <li>Use stolen or unauthorized payment methods or bank accounts.</li>
              <li>Interfere with, disrupt, or attempt to gain unauthorized access to the Platform.</li>
              <li>Scrape, reverse engineer, or copy the Platform except as permitted by law.</li>
            </ul>
          </Section>

          <Section id="termination" number="7" title="Account Deletion and Termination">
            <p>
              You may delete your account at any time from the Profile screen in the app. Deleting your
              account stops future recurring donations and round-ups and removes your personal
              information, except records we must retain for financial, tax, or legal purposes as
              described in our{' '}
              <Link to="/privacy" className="text-gold hover:underline">Privacy Policy</Link>. We may
              suspend or terminate your access at any time if you violate these Terms.
            </p>
          </Section>

          <Section id="license" number="8" title="License and Intellectual Property">
            <p>
              Subject to these Terms, Claritas grants you a limited, non-exclusive, non-transferable,
              revocable license to use the Platform for personal, non-commercial purposes. The Platform
              and its content, other than User Content and parish content, are owned by Claritas and its
              licensors.
            </p>
            <p>
              If you download our mobile app from the Apple App Store, you acknowledge that these Terms
              are between you and Claritas only, not Apple; Apple is not responsible for the app or its
              content, has no obligation to provide maintenance or support, and is not responsible for
              addressing any claims relating to the app. Apple and its subsidiaries are third-party
              beneficiaries of these Terms and may enforce them against you. Your use of the app must
              also comply with the App Store's usage rules.
            </p>
          </Section>

          <Section id="disclaimers" number="9" title="Disclaimers and Limitation of Liability">
            <p>
              The Platform is provided "as is" and "as available" without warranties of any kind, to the
              fullest extent permitted by law. Claritas does not verify how parishes use donated funds
              and is not responsible for the content or conduct of parishes or other users. To the
              fullest extent permitted by law, Claritas will not be liable for any indirect, incidental,
              special, consequential, or punitive damages, and our total liability for any claim arising
              from the Platform will not exceed the greater of the fees you paid to Claritas in the
              twelve months before the claim or $100.
            </p>
          </Section>

          <Section id="changes" number="10" title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we make material changes, we will update
              the Effective Date above and, where appropriate, provide additional notice. Your continued
              use of the Platform after an update constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section id="contact" number="11" title="Contact Us">
            <p>Questions about these Terms, reports of objectionable content, or support requests:</p>
            <p>
              <strong className="text-charcoal">Claritas Giving</strong>
              <br />
              Email:{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a>
            </p>
          </Section>
        </div>
      </section>
    </div>
  )
}
